<div class="sla-container">
  <div class="sla-header">
    <div>
      <h2><i class="fa-solid fa-wand-magic-sparkles"></i> Automation Rules</h2>
      <p class="sla-sub">When a ticket event fires, active rules for that event run in order; a rule whose conditions match applies its actions (set a field, transition status, assign an agent, add a comment). Wired events: ticket created, status changed, assignee changed, escalated, SLA breached.</p>
    </div>
    <button class="btn-primary" (click)="startCreate()" *ngIf="!isCreating">
      <i class="fa-solid fa-plus"></i> New Rule
    </button>
  </div>

  <div class="banner banner-error" *ngIf="errorMessage">{{ errorMessage }}</div>
  <div class="banner banner-success" *ngIf="successMessage">{{ successMessage }}</div>

  <!-- Create -->
  <div class="policy-card editing" *ngIf="isCreating">
    <ng-container *ngTemplateOutlet="ruleForm; context: { form: createForm }"></ng-container>
    <div class="form-actions">
      <button class="btn-primary" (click)="saveCreate()" [disabled]="isSaving">Create</button>
      <button class="btn-ghost" (click)="cancelCreate()">Cancel</button>
    </div>
  </div>

  <div class="sla-loading" *ngIf="isLoading"><i class="fa-solid fa-spinner fa-spin"></i> Loading rules…</div>

  <div class="sla-empty" *ngIf="!isLoading && rules.length === 0 && !isCreating">
    <i class="fa-solid fa-wand-magic-sparkles"></i>
    <p>No automation rules yet. Create one to react to ticket events automatically.</p>
  </div>

  <!-- Rule list -->
  <div class="policy-card" [class.editing]="d.isEditing" *ngFor="let d of rules">
    <div class="policy-row" *ngIf="!d.isEditing">
      <div class="policy-main">
        <span class="policy-name">{{ d.rule.Name }}</span>
        <span class="policy-meta">
          <span class="metric"><i class="fa-solid fa-bolt"></i> {{ triggerLabel(d.rule.TriggerEvent) }}</span>
          <span class="metric"><i class="fa-solid fa-list-ol"></i> Seq {{ d.rule.Sequence }}</span>
          <span class="metric"><i class="fa-solid fa-gears"></i> {{ actionsSummary(d) }}</span>
        </span>
      </div>
      <span class="status-chip" [class.inactive]="!d.rule.IsActive">{{ d.rule.IsActive ? 'Active' : 'Inactive' }}</span>
      <div class="policy-actions">
        <button class="icon-btn" title="Edit" (click)="startEdit(d)"><i class="fa-solid fa-pen"></i></button>
        <button class="icon-btn danger" title="Delete" (click)="confirmDelete(d)"><i class="fa-solid fa-trash"></i></button>
      </div>
    </div>

    <div *ngIf="d.isEditing">
      <ng-container *ngTemplateOutlet="ruleForm; context: { form: d.form }"></ng-container>
      <div class="form-actions">
        <button class="btn-primary" (click)="saveEdit(d)" [disabled]="isSaving">Save</button>
        <button class="btn-ghost" (click)="cancelEdit(d)">Cancel</button>
      </div>
    </div>
  </div>
</div>

<!-- ===== shared rule form ===== -->
<ng-template #ruleForm let-form="form">
  <div class="form-grid">
    <label>Name<input type="text" [(ngModel)]="form.name" placeholder="e.g. Escalate Overdue Critical" /></label>
    <label>When (event)
      <select [(ngModel)]="form.triggerEvent">
        <option *ngFor="let t of triggers" [value]="t.value" [disabled]="!t.wired">{{ t.label }}{{ t.wired ? '' : ' (coming soon)' }}</option>
      </select>
    </label>
    <label>Sequence<input type="number" [(ngModel)]="form.sequence" title="Lower runs first" /></label>
    <label class="checkbox-field"><input type="checkbox" [(ngModel)]="form.isActive" /> Active</label>
    <label class="full-row">Description<input type="text" [(ngModel)]="form.description" placeholder="Optional" /></label>
  </div>

  <!-- Conditions -->
  <div class="rule-section">
    <div class="rule-section-head">
      <span class="rule-section-title">Conditions</span>
      <label class="inline-select">Match
        <select [(ngModel)]="form.operator">
          <option value="AND">ALL of</option>
          <option value="OR">ANY of</option>
        </select>
      </label>
    </div>
    <p class="hint-line" *ngIf="form.conditions.length === 0">No conditions — runs on every {{ triggerLabel(form.triggerEvent) }} event.</p>
    <div class="condition-row" *ngFor="let c of form.conditions; let i = index">
      <select [(ngModel)]="c.field">
        <option *ngFor="let f of fields" [value]="f.value">{{ f.label }}</option>
      </select>
      <select [(ngModel)]="c.op">
        <option *ngFor="let o of ops" [value]="o.value">{{ o.label }}</option>
      </select>
      <input type="text" [(ngModel)]="c.value" placeholder="value" />
      <button class="icon-btn danger" title="Remove" (click)="removeCondition(form, i)"><i class="fa-solid fa-xmark"></i></button>
    </div>
    <button class="btn-ghost btn-sm" (click)="addCondition(form)"><i class="fa-solid fa-plus"></i> Add condition</button>
  </div>

  <!-- Actions -->
  <div class="rule-section">
    <div class="rule-section-head"><span class="rule-section-title">Actions</span></div>
    <div class="condition-row action-row" *ngFor="let a of form.actions; let i = index">
      <select [(ngModel)]="a.type">
        <option *ngFor="let t of actionTypes" [value]="t.value">{{ t.label }}</option>
      </select>
      <!-- set_field: field + value -->
      <ng-container *ngIf="a.type === 'set_field'">
        <select [(ngModel)]="a.field">
          <option value="Priority">Priority</option>
          <option value="Severity">Severity</option>
        </select>
        <select [(ngModel)]="a.value">
          <option value="">—</option>
          <option value="Low">Low</option><option value="Medium">Medium</option>
          <option value="High">High</option><option value="Critical">Critical</option>
        </select>
      </ng-container>
      <!-- transition_status: status name -->
      <input *ngIf="a.type === 'transition_status'" type="text" [(ngModel)]="a.value" placeholder="status name (e.g. Escalated)" class="action-wide" />
      <!-- assign_agent: pick an org member (not a raw Person ID) -->
      <select *ngIf="a.type === 'assign_agent'" [(ngModel)]="a.personId" class="action-wide">
        <option value="">Select agent…</option>
        <option *ngFor="let p of assignablePeople" [value]="p.id">{{ p.name }}</option>
      </select>
      <!-- add_comment: content -->
      <input *ngIf="a.type === 'add_comment'" type="text" [(ngModel)]="a.content" placeholder="internal comment text" class="action-wide" />
      <button class="icon-btn danger" title="Remove" (click)="removeAction(form, i)"><i class="fa-solid fa-xmark"></i></button>
    </div>
    <button class="btn-ghost btn-sm" (click)="addAction(form)"><i class="fa-solid fa-plus"></i> Add action</button>
  </div>
</ng-template>
